import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { generateRedirectUrl } from '@connected/api-client/lib/portal';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Calendar } from '@connected/ui/components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@connected/ui/components/popover';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import L from 'leaflet';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, MoveUpRight, UsersRound } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import z from 'zod';

import { ResizableContainer } from '@/components/resizable-container';
import { cn } from '@/lib/utils';

const DEFAULT_INSPECTOR_COLOR = '#DF5617';

function createCircleIcon(color: string) {
    const svg = `
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="${color}" fill-opacity="0.3" stroke="${color}" stroke-width="2"/>
            <circle cx="12" cy="12" r="5" fill="${color}"/>
        </svg>
    `;
    return L.divIcon({
        html: svg,
        className: 'custom-circle-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
    });
}

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/',
)({
    component: RouteComponent,
    // beforeLoad: async ({ params, context }) => {
    //     const hasCorrectRole = context.currentUser?.Roles?.some(
    //         role => role?.Name === 'Applicatie beheerder' || role?.Name === 'Elektra manager' || role?.Name === 'Elektra bevoegd inspecteur' || role?.Name === 'Elektra inspecteur',
    //     );
    //     if (!hasCorrectRole && !context.currentUser?.IsAdmin) {
    //         throw redirect({ to: '/dashboard/organizations/$organizationId/users', params });
    //     }
    //     throw redirect({ to: '/dashboard/organizations/$organizationId/requests', params });
    // },
    validateSearch: z.object({
        search: z.string().optional(),
        tab: z.enum(['OwnInspections', 'AllInspections']).optional(),
    }),
});

type ProjectData = {
    projectVersionId: string;
    status?: string | null;
    inspectionKind?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    objectName?: string | null;
    inspectors?: Array<{ DisplayName?: string | null; Kind?: string | null; UserId?: string | null; ColorCode?: string | null }> | null;
    markerColor?: string;
};

type MarkerData = {
    position: [number, number];
    locality?: string | null;
    postalCode?: string | null;
    street?: string | null;
    streetNumber?: string | null;
    projects: ProjectData[];
    markerColor?: string;
};

function formatInspectionKind(reports?: Array<{ InspectionKind?: string | null }> | null): string {
    if (!reports || reports.length === 0)
        return '-';

    if (reports.length > 1) {
        if (reports.every(x => x.InspectionKind?.toString().toLowerCase().includes('scope'))) {
            const sorted = [...reports].sort((a, b) => {
                const an = a.InspectionKind?.match(/\d+/)?.[0];
                const bn = b.InspectionKind?.match(/\d+/)?.[0];
                return an && bn ? Number(an) - Number(bn) : an ? -1 : bn ? 1 : 0;
            });
            const numbers = sorted.map(x => x.InspectionKind?.replace(/\D+/g, '')).filter(Boolean);
            return `Scope ${numbers.join('-')}`;
        }
        return reports.map(x => x.InspectionKind).filter(Boolean).join('-');
    }

    return reports[0]?.InspectionKind?.replace(/([A-Z])(?=\d)/gi, '$1 ') ?? '-';
}

function formatDateRange(startDate?: string | null, endDate?: string | null): string {
    if (!startDate)
        return '';

    const start = new Date(startDate);
    const hasStartTime = startDate.includes('T') && !startDate.includes('T00:00:00');

    const startFormatted = hasStartTime
        ? format(start, 'd MMM yyyy HH:mm')
        : format(start, 'd MMM yyyy');

    if (!endDate)
        return startFormatted;

    const end = new Date(endDate);
    const hasEndTime = endDate.includes('T') && !endDate.includes('T00:00:00');

    const sameDay = start.toDateString() === end.toDateString();

    if (sameDay && hasEndTime) {
        return `${startFormatted} - ${format(end, 'HH:mm')}`;
    }

    const endFormatted = hasEndTime
        ? format(end, 'd MMM yyyy HH:mm')
        : format(end, 'd MMM yyyy');

    return `${startFormatted} - ${endFormatted}`;
}

function ProjectItem({ project, location, t }: { project: ProjectData; location?: { street?: string | null; streetNumber?: string | null; postalCode?: string | null; locality?: string | null }; t: (key: string) => string }) {
    // Get inspector names
    const inspectorNames = useMemo(() => {
        const filtered = project.inspectors?.filter(u => u.Kind === 'Inspector' || u.Kind === 'CertifiedInspector') || [];
        return [...new Set(filtered.map(u => u.DisplayName).filter(Boolean))];
    }, [project.inspectors]);

    const dateRange = formatDateRange(project.startDate, project.endDate);
    const hasLocation = location?.street && location?.streetNumber && location?.postalCode && location?.locality;

    // Title: use objectName or "Geen locatiegegevens"
    const title = project.objectName || 'Geen locatiegegevens';

    return (
        <div className="flex flex-col">
            <div className="font-semibold text-lg">{title}</div>
            {project.inspectionKind && (
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {project.inspectionKind}
                </div>
            )}
            <div className="flex items-center gap-2 text-sm mb-0.5">
                <span className="font-medium">
                    {project.status ? t(`dashboard.projects.table.columns.status.${project.status.toLowerCase()}`) : '-'}
                </span>
            </div>
            {dateRange && (
                <div className="text-muted-foreground text-xs mb-1">{dateRange}</div>
            )}
            {hasLocation && (
                <div className="flex flex-row gap-2 mt-1">
                    <MapPin size={16} className="mt-0.5 text-foreground/60" />
                    <div className="text-sm flex items-start gap-2">
                        <span>{`${location.street} ${location.streetNumber}, ${location.postalCode} ${location.locality}`}</span>
                    </div>
                </div>
            )}
            {inspectorNames.length > 0 && (
                <div className="flex flex-row gap-2 mt-0.5">
                    <UsersRound size={18} className="mt-0.5 text-foreground/60 inline" />
                    <div className="text-sm inline gap-2">
                        <span>
                            {inspectorNames.length > 3
                                ? `${inspectorNames.slice(0, 3).join(', ')}...`
                                : inspectorNames.join(', ')}
                        </span>
                    </div>
                </div>
            )}
            <a
                href={`${generateRedirectUrl({
                    redirectHost: import.meta.env.VITE_DOMAIN_ELEKTRA_APP,
                    redirectPath: `/projects/${project.projectVersionId}/applications/request-concerning`,
                })}`}
                target="_blank"
                rel="noopener noreferrer"
                className="self-end"
            >
                <p className="mt-3 text-sm text-brand-primary font-semibold hover:cursor-pointer">
                    {t('dashboard.planning.message.openInElectra')}
                    <MoveUpRight size={16} className="inline ml-1" />
                </p>
            </a>
        </div>
    );
}

function MarkerWithHover({ data }: { data: MarkerData }) {
    const markerRef = useRef<any>(null);
    const { t } = useTranslation('dashboard');
    // Use the inspector color (like in planning) - gray for multiple inspectors, orange as default
    const markerColor = data.markerColor ?? DEFAULT_INSPECTOR_COLOR;
    const icon = createCircleIcon(markerColor);

    const location = {
        street: data.street,
        streetNumber: data.streetNumber,
        postalCode: data.postalCode,
        locality: data.locality,
    };

    return (
        <Marker
            ref={markerRef}
            position={data.position}
            icon={icon}
            eventHandlers={{
                mouseover: () => {
                    markerRef.current?.openPopup();
                },
                click: () => {
                    markerRef.current?.openPopup();
                },
            }}
        >
            <Popup closeButton={true} autoClose={false} className="project-marker-popup">
                <div className={`flex flex-col ${data.projects.length > 1 ? 'divide-y divide-border' : ''}`}>
                    {data.projects.map(project => (
                        <div key={project.projectVersionId} className={data.projects.length > 1 ? 'py-3 first:pt-0 last:pb-0' : ''}>
                            <ProjectItem project={project} location={location} t={t} />
                        </div>
                    ))}
                </div>
            </Popup>
        </Marker>
    );
}

function RouteComponent() {
    const { t, i18n } = useTranslation('dashboard');
    const [mapKey, setMapKey] = useState(Date.now());
    const params = Route.useParams();
    const search = Route.useSearch();
    const { currentUser } = useAuthentication();
    const resizableContainerTarget = useRef<HTMLDivElement>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const dateFnsLocale = useMemo(() => {
        const lang = i18n.language || 'en-GB';
        if (lang.startsWith('nl'))
            return nl;
        return enGB;
    }, [i18n.language]);

    // Use planning query for selected date's projects
    const planningQuery = useQuery({
        ...portaalQueryOptions.electrical.planning.planningList({
            organizationId: params.organizationId,
            periodKind: 'OneDay',
            startDate: format(selectedDate, 'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX'),
        }),
    });

    // Filter projects that have coordinates
    const projectsWithCoordinates = useMemo(() => {
        if (!planningQuery?.data?.Projects)
            return [];

        const filtered = planningQuery.data.Projects.filter(
            project => project?.ProjectObject?.Street || project?.ProjectObject?.Locality,
        );

        // Filter by tab: OwnInspections shows only user's inspections, AllInspections shows all
        const activeTabValue = search.tab ?? 'AllInspections';
        if (activeTabValue === 'OwnInspections') {
            return filtered.filter(project =>
                project?.ProjectUsers?.some(user => user?.UserId === currentUser?.UserId),
            );
        }
        return filtered;
    }, [currentUser?.UserId, search.tab, planningQuery?.data?.Projects]);

    // Fetch coordinates for each project
    const coordinates = useQueries({
        queries: projectsWithCoordinates.map(project => ({
            ...elektraAppQueryOptions.project.request.objectOptions({
                electricalProjectVersionId: project?.ElectricalProjectVersionId ?? '',
            }),
            enabled: !!project?.ElectricalProjectVersionId,
        })),
    });

    const tabsKind: { value: string; label: string }[] = [
        {
            value: 'AllInspections',
            label: t('dashboard.dashboard.tabs.allInspections'),
        },
        {
            value: 'OwnInspections',
            label: t('dashboard.dashboard.tabs.ownInspections'),
        },
    ];

    const activeTab = search.tab ?? 'AllInspections';

    // Combine planning data with coordinates
    const projectsWithData = useMemo(() => {
        return coordinates.map((coord, index) => ({
            coordinates: coord?.data?.ProjectObject?.Coordinate,
            objectNameFromElektra: coord?.data?.ProjectObject?.Name,
            project: projectsWithCoordinates[index],
        }));
    }, [coordinates, projectsWithCoordinates]);

    // Group markers by coordinates to handle multiple projects at same location
    const groupedMarkers = useMemo(() => {
        const groups = new Map<string, MarkerData>();

        projectsWithData?.forEach((item) => {
            const lat = item?.coordinates?.GpsLatitude;
            const lng = item?.coordinates?.GpsLongitude;
            const project = item?.project;

            if (!lat || !lng || !project)
                return;

            const key = `${lat},${lng}`;

            // Calculate inspector color like in planning
            const uniqueInspectors = project.ProjectUsers?.filter((user, index, self) =>
                index === self.findIndex(t => t.UserId === user.UserId),
            ) ?? [];

            let projectMarkerColor: string;
            if (uniqueInspectors.length > 1) {
                // Multiple inspectors -> use primary color
                projectMarkerColor = DEFAULT_INSPECTOR_COLOR;
            }
            else if (uniqueInspectors.length === 1 && uniqueInspectors[0]?.ColorCode) {
                // Single inspector with color -> use their color
                projectMarkerColor = uniqueInspectors[0].ColorCode;
            }
            else {
                // No inspector or no color -> default primary color
                projectMarkerColor = DEFAULT_INSPECTOR_COLOR;
            }

            const projectData: ProjectData = {
                projectVersionId: project.ElectricalProjectVersionId ?? '',
                status: project.Status,
                inspectionKind: formatInspectionKind(project.Reports),
                startDate: project.StartDate,
                endDate: project.EndDate,
                // Priority: 1. elektra-app objectOptions, 2. planning query, 3. fallback
                objectName: item.objectNameFromElektra || project.ProjectObject?.Name,
                inspectors: project.ProjectUsers,
                markerColor: projectMarkerColor,
            };

            if (groups.has(key)) {
                const existingGroup = groups.get(key)!;
                existingGroup.projects.push(projectData);
                // If multiple projects at same location, use primary color
                if (existingGroup.projects.length > 1) {
                    existingGroup.markerColor = DEFAULT_INSPECTOR_COLOR;
                }
            }
            else {
                groups.set(key, {
                    position: [lat, lng],
                    locality: project.ProjectObject?.Locality,
                    postalCode: project.ProjectObject?.PostalCode,
                    street: project.ProjectObject?.Street,
                    streetNumber: project.ProjectObject?.StreetNumber,
                    projects: [projectData],
                    markerColor: projectMarkerColor,
                });
            }
        });

        return Array.from(groups.values());
    }, [projectsWithData]);

    return (
        <div className="flex flex-col gap-8 justify-start">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                <div className="flex flex-col sm:flex-row justify-between w-full gap-4">
                    <div>
                        <h3 className="text-2xl font-semibold">
                            {t('dashboard.dashboard.title')}
                        </h3>
                        <p className="text-md text-text-primary/70">
                            {t('dashboard.dashboard.subtitle')}
                        </p>
                    </div>
                    {/* Tabs and date picker - stacks below title on small screens */}
                    <div className="flex flex-row items-center sm:pr-5 gap-1 sm:gap-2">
                        {currentUser?.Roles?.find((role) => {
                            const name = role?.Name?.toLocaleLowerCase() ?? '';
                            return name.includes('eigenaar') || name.includes('manager') || name.includes('beheerder');
                        }) && (
                            <div className="flex flex-row">
                                {tabsKind.map((item, index) => (
                                    <Link
                                        key={item.value}
                                        to="/dashboard/organizations/$organizationId"
                                        params={{ organizationId: params?.organizationId }}
                                        search={{
                                            tab: item.value as 'OwnInspections' | 'AllInspections' ?? undefined,
                                            search: search.search,
                                        }}
                                        className="hidden sm:block"
                                    >
                                        <Button
                                            size="sm"
                                            style={
                                                index === 0
                                                    ? { borderRadius: '10px 0px 0px 10px ', borderLeft: '1px solid hsl(var(--border-primary)' }
                                                    : index === tabsKind.length - 1
                                                        ? { borderRadius: '0px 10px 10px 0px' }
                                                        : { borderRadius: '0px' }
                                            }
                                            className={`border-border-primary border-t border-r border-b bg-background-active hover:bg-active text-xs md:text-sm px-2 md:px-3 ${item.value === activeTab ? ' text-secondary_hover font-bold' : 'text-secondary-700 bg-background-primary'}`}
                                        >
                                            {item.label}
                                        </Button>
                                    </Link>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-row items-center">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 md:h-9 md:w-9"
                                onClick={() => setSelectedDate(prev => subDays(prev, 1))}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className={cn(
                                            'justify-start text-left font-normal text-xs md:text-sm px-2 md:px-3',
                                            !selectedDate && 'text-muted-foreground',
                                        )}
                                    >
                                        <CalendarIcon className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
                                        {selectedDate ? format(selectedDate, 'd MMM yyyy', { locale: dateFnsLocale }) : t('dashboard.dashboard.selectDate')}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2" align="end">
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        defaultMonth={selectedDate}
                                        onSelect={(date) => {
                                            if (date) {
                                                setSelectedDate(startOfDay(date));
                                                setDatePickerOpen(false);
                                            }
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 md:h-9 md:w-9"
                                onClick={() => setSelectedDate(prev => addDays(prev, 1))}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <ResizableContainer
                targetElement={resizableContainerTarget}
                resizeFunction={() => setMapKey(Date.now())}
            >
                <div
                    className="flex flex-col lg:flex-1 lg:flex-row-reverse gap-2 xl:gap-4"
                    ref={resizableContainerTarget}
                >
                    <div className="w-full h-[50vh] sm:h-[55vh] md:h-[65vh] lg:h-[70vh] min-h-[550px]">
                        <MapContainer
                            key={mapKey}
                            center={[52.1326, 5.2913]}
                            zoom={7}
                            className="h-full w-full rounded-xl !z-0"
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
                            />
                            <MarkerClusterGroup
                                showCoverageOnHover
                                disableClusteringAtZoom={18}
                                spiderfyOnMaxZoom={false}
                                removeOutsideVisibleBounds
                                maxClusterRadius={80}
                            >
                                {groupedMarkers.map(markerData => (
                                    <MarkerWithHover
                                        key={`${markerData.position[0]},${markerData.position[1]}`}
                                        data={markerData}
                                    />
                                ))}
                            </MarkerClusterGroup>
                        </MapContainer>
                    </div>
                </div>
            </ResizableContainer>
        </div>
    );
}
